import { useState, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from 'sonner';
import { HelmetProvider } from 'react-helmet-async';
import { NotificationBar } from './components/NotificationBar';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { BackToTop } from './components/BackToTop';
import { ExitIntentNewsletter } from './components/NewsletterPrompts';
import EngagementTracker from './components/EngagementTracker';
import { HomePage } from './pages/HomePage';

const ExitIntentNewsletterWrapper = () => {
  const location = useLocation();
  if (location.pathname.startsWith('/admin') || location.pathname === '/summerfest') return null;
  // (Msg 716) Skip the exit-intent popup when embedded inside the TV mockup
  // iframe — it would slam over the showcase content the partner is watching.
  if (new URLSearchParams(location.search).get('embed') === '1') return null;
  return <ExitIntentNewsletter subscriberCount={3200} />;
};

// (Msg 776) Preserve country / city url segments when redirecting the legacy
// /listings/:country/:city paths to the canonical /events/... equivalent.
const NavigateListingsToEvents = () => {
  const location = useLocation();
  const tail = location.pathname.replace(/^\/listings/, '');
  return <Navigate to={`/events${tail}${location.search}`} replace />;
};

// (Msg 790) Same pattern for /things-to-do → /experiences — preserves
// per-city slug so /things-to-do/toronto lands at /experiences/toronto.
const NavigateThingsToDoToExperiences = () => {
  const location = useLocation();
  const tail = location.pathname.replace(/^\/things-to-do/, '');
  return <Navigate to={`/experiences${tail}${location.search}`} replace />;
};

// (Msg 716) When the page is loaded inside the `/things-to-watch` TV mockup
// iframe via `?embed=1`, strip the global chrome (header / notification bar /
// footer / back-to-top button / exit-intent) so only the route's own content
// renders — an exact, distraction-free mirror.
const ChromeAwareLayout = ({ notifVisible, setNotifVisible }) => {
  const location = useLocation();
  const isEmbed = new URLSearchParams(location.search).get('embed') === '1';
  if (isEmbed) return null;
  return (
    <>
      <NotificationBar onVisibilityChange={setNotifVisible} />
      <Header notifVisible={notifVisible} />
      <BackToTop />
    </>
  );
};

const FooterChrome = () => {
  const location = useLocation();
  const isEmbed = new URLSearchParams(location.search).get('embed') === '1';
  if (isEmbed) return null;
  return <Footer />;
};
import { ListingsPage } from './pages/ListingsPage';
import { BlogPage } from './pages/BlogPage';
import GameHighlightsPage from './pages/GameHighlightsPage';
import { LanguageProvider } from './contexts/LanguageContext';

// Lazy-loaded pages — not needed on initial load
const VenueRegistrationPage = lazy(() => import('./pages/VenueRegistrationPage').then(m => ({ default: m.VenueRegistrationPage })));
const ReferVenuePage = lazy(() => import('./pages/ReferVenuePage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const CareersPage = lazy(() => import('./pages/CareersPage'));
const MeetTheTeamPage = lazy(() => import('./pages/MeetTheTeamPage'));
const MatchSchedulePage = lazy(() => import('./pages/MatchSchedulePage'));
const AffiliatePage = lazy(() => import('./pages/AffiliatePage'));
const AffiliateSharePage = lazy(() => import('./pages/AffiliateSharePage'));
const AdvertisePage = lazy(() => import('./pages/AdvertisePage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const LandAcknowledgementPage = lazy(() => import('./pages/LandAcknowledgementPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const SponsorPage = lazy(() => import('./pages/SponsorPage'));
const CommunityPackagesPage = lazy(() => import('./pages/CommunityPackagesPage'));
const SummerfestPage = lazy(() => import('./pages/SummerfestPage'));
const ThingsToDoPage = lazy(() => import('./pages/ThingsToDoPage'));
const TorontoGuidePage = lazy(() => import('./pages/TorontoGuidePage'));
const AdvertiseSuccessPage = lazy(() => import('./pages/AdvertiseSuccessPage'));
const PublicFormPage = lazy(() => import('./pages/PublicFormPage'));
const AssigneeFormPage = lazy(() => import('./pages/AssigneeFormPage'));
const PartnerVenuesPage = lazy(() => import('./pages/PartnerVenuesPage'));
const PremiumPartnershipsPage = lazy(() => import('./pages/PremiumPartnershipsPage'));
const PartnershipsHubPage = lazy(() => import('./pages/PartnershipsHubPage'));

const ScrollToTop = () => {
  const { pathname } = useLocation();
  // Disable the browser's auto scroll-restoration once. Without this, when
  // the user hits Back, Chrome/Safari restore the previous scroll position
  // (often the footer) AFTER our effect fires — landing them at the bottom.
  // Forcing "manual" lets our scrollTo(0,0) below always win on every
  // navigation (push, replace, or popstate / Back button).
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// (Msg 761) Redirect helper for typo URLs (/thing → /things-to-do, etc.).
// Plain <Navigate replace> swaps the path during render, BUT because the
// pathname changes synchronously inside render, `ScrollToTop`'s pathname
// effect may have already fired for the prior route. We schedule an extra
// post-mount scroll(0,0) so users always land at the top of the destination
// page — never at the footer position they may have clicked from.
const RedirectAndScroll = ({ to }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <Navigate to={to} replace />;
};

function App() {
  const [notifVisible, setNotifVisible] = useState(true);

  return (
    <HelmetProvider>
    <LanguageProvider>
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <EngagementTracker />
        <ChromeAwareLayout notifVisible={notifVisible} setNotifVisible={setNotifVisible} />
        <Suspense fallback={<div className="min-h-screen pt-36 flex justify-center"><div className="animate-pulse"><div className="h-8 bg-gray-200 rounded w-48 mx-auto mb-4" /><div className="h-4 bg-gray-200 rounded w-64 mx-auto" /></div></div>}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          {/* (Msg 776) Public route renamed from /listings → /events. The old
              /listings paths are kept as 301-style redirects so external
              backlinks (Eventbrite, blog backlinks, things-to-do CTAs) keep
              working. Component still named ListingsPage internally. */}
          <Route path="/events" element={<ListingsPage />} />
          <Route path="/events/:country" element={<ListingsPage />} />
          <Route path="/events/:country/:city" element={<ListingsPage />} />
          <Route path="/listings" element={<Navigate to="/events" replace />} />
          <Route path="/listings/:country" element={<NavigateListingsToEvents />} />
          <Route path="/listings/:country/:city" element={<NavigateListingsToEvents />} />
          <Route path="/venue-registration" element={<VenueRegistrationPage />} />
          <Route path="/refer-venue" element={<ReferVenuePage />} />
          <Route path="/refer-event" element={<ReferVenuePage />} />
          <Route path="/blog" element={<BlogPage />} />
          {/* (Msg 746) Game Highlights — must come BEFORE /blog/:slug so that
              /blog/game-highlights and /blog/game-highlights/:slug match
              the highlight routes, not the generic blog detail. */}
          <Route path="/blog/game-highlights" element={<GameHighlightsPage />} />
          <Route path="/blog/game-highlights/:slug" element={<GameHighlightsPage />} />
          <Route path="/blog/:slug" element={<BlogPage />} />
          <Route path="/guides/:slug" element={<BlogPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/meet-the-team" element={<MeetTheTeamPage />} />
          <Route path="/affiliate" element={<AffiliatePage />} />
          <Route path="/affiliate/share/:code" element={<AffiliateSharePage />} />
          <Route path="/p/:code" element={<AffiliateSharePage />} />
          <Route path="/advertise" element={<AdvertisePage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/land-acknowledgement" element={<LandAcknowledgementPage />} />
          <Route path="/media/kit/sponsorships/2026" element={<AdvertisePage />} />
          <Route path="/canada" element={<ListingsPage />} />
          <Route path="/usa" element={<ListingsPage />} />
          <Route path="/mexico" element={<ListingsPage />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/tos" element={<TermsOfServicePage />} />
          <Route path="/sponsors" element={<SponsorPage />} />
          <Route path="/sponsors/community-packages" element={<CommunityPackagesPage />} />
          <Route path="/summerfest" element={<SummerfestPage />} />
          {/* (Msg 790) Rename /things-to-do → /experiences (canonical). Old
              /things-to-do* URLs 301-redirect via NavigateThingsToDoToExperiences
              so external backlinks + blog CTAs keep working. Nav labels
              still read "Things To Do" but link to /experiences. */}
          <Route path="/experiences" element={<ThingsToDoPage />} />
          <Route path="/experiences/toronto" element={<TorontoGuidePage />} />
          <Route path="/experiences/:citySlug" element={<TorontoGuidePage />} />
          <Route path="/things-to-do" element={<Navigate to="/experiences" replace />} />
          <Route path="/things-to-do/:citySlug" element={<NavigateThingsToDoToExperiences />} />
          <Route path="/explore" element={<Navigate to="/experiences" replace />} />
          <Route path="/explore-cities" element={<Navigate to="/experiences" replace />} />
          <Route path="/my-trip" element={<TorontoGuidePage tripView />} />
          <Route path="/advertise/success" element={<AdvertiseSuccessPage />} />
          <Route path="/forms/assignee/:assigneeToken" element={<AssigneeFormPage />} />
          <Route path="/forms/:shareToken" element={<PublicFormPage />} />
          <Route path="/partner-venues" element={<PartnerVenuesPage />} />
          <Route path="/things-to-watch" element={<PartnerVenuesPage />} />
          <Route path="/premium-partnerships" element={<PremiumPartnershipsPage />} />
          {/* (Msg 764) /partnerships — hidden social-outreach landing hub. */}
          <Route path="/partnerships" element={<PartnershipsHubPage />} />
          <Route path="/venue-partner" element={<RedirectAndScroll to="/partnerships" />} />
          {/* (Msg 769) Additional aliases that all funnel to /partnerships. */}
          <Route path="/venue-partners" element={<RedirectAndScroll to="/partnerships" />} />
          <Route path="/partner-venue" element={<RedirectAndScroll to="/partnerships" />} />
          <Route path="/partner" element={<RedirectAndScroll to="/partnerships" />} />
          <Route path="/pricing" element={<RedirectAndScroll to="/partnerships" />} />
          {/* (Msg 761) Typo redirects — /things-to-do gets the standalone
              /thing & /things since "things" is plural for "things to do". */}
          <Route path="/thing" element={<RedirectAndScroll to="/things-to-do" />} />
          <Route path="/things" element={<RedirectAndScroll to="/things-to-do" />} />
          <Route path="/thing-to-do" element={<RedirectAndScroll to="/things-to-do" />} />
          <Route path="/thingstodo" element={<RedirectAndScroll to="/things-to-do" />} />
          <Route path="/thingtodo" element={<RedirectAndScroll to="/things-to-do" />} />
          <Route path="/thing2do" element={<RedirectAndScroll to="/things-to-do" />} />
          <Route path="/things2do" element={<RedirectAndScroll to="/things-to-do" />} />
          <Route path="/thing-to-watch" element={<RedirectAndScroll to="/things-to-watch" />} />
          <Route path="/thingstowatch" element={<RedirectAndScroll to="/things-to-watch" />} />
          <Route path="/thingtowatch" element={<RedirectAndScroll to="/things-to-watch" />} />
          <Route path="/thing2watch" element={<RedirectAndScroll to="/things-to-watch" />} />
          <Route path="/things2watch" element={<RedirectAndScroll to="/things-to-watch" />} />
          <Route path="/match-schedule" element={<MatchSchedulePage />} />
        </Routes>
        </Suspense>
        <FooterChrome />
        <ExitIntentNewsletterWrapper />
      </BrowserRouter>
      <Toaster position="top-right" theme="light" richColors />
    </div>
    </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
